/**
 * Dictionnaire i18n du silo CONTACT (FR/EN).
 *
 * IMPORTANT : ce module ne contient QUE des libellés d'affichage + messages de
 * validation. La logique (état du formulaire, conditions de validation, shape
 * du payload, endpoint `/api/contact`) n'est PAS touchée — seuls les textes
 * deviennent localisés. Défaut `fr` → zéro régression FR (sortie identique).
 *
 * FR = copie exacte du code existant. EN = anglais premium et naturel.
 *
 * - `phonePattern` : motif HTML `pattern` du champ téléphone. FR = format
 *   français strict (identique à l'actuel) ; EN = motif international (même
 *   approche INTL_PHONE que ReservationForm / QuickBookingForm).
 * - `useCases` : libellés des types de demande (les ids/enums restent en FR
 *   côté API). Voir `getUseCases(locale)` dans constants.ts.
 */
import { type I18n } from "@/config/localeText";
import type { UseCaseId } from "@/(global)/contact/components/types";

/** Motif téléphone FR (strict) — identique au ContactForm d'origine. */
const FR_PHONE_PATTERN = "^(?:(?:\\+|00)33|0)\\s*[1-9](?:[\\s.\\-]*\\d{2}){4}$";
/** Motif téléphone international (EN) — même approche que les autres formulaires. */
const INTL_PHONE_PATTERN = "^\\+?[0-9][0-9\\s.\\-()]{6,}$";

export type ContactUseCaseCopy = {
  id: UseCaseId;
  label: string;
  subtitle: string;
  subject: string;
};

export type ContactUi = {
  hero: {
    eyebrow: string;
    title: string;
    intro: string;
    whatsapp: string;
    email: string;
  };
  selector: {
    step: string;
    heading: string;
    help: string;
  };
  useCases: ContactUseCaseCopy[];
  form: {
    intro: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    phonePlaceholder: string;
    phoneTitle: string;
    phonePattern: string;
    subject: string;
    message: string;
    messagePlaceholder: string;
    quote: string;
    detailsSummary: string;
    company: string;
    eventName: string;
    pickup: string;
    dropoff: string;
    date: string;
    time: string;
    passengers: string;
    vehicle: string;
    vehiclePlaceholder: string;
    budget: string;
    budgetPlaceholder: string;
    honeypotLabel: string;
    sending: string;
    submit: string;
    backHome: string;
    success: string;
    genericError: string;
    unexpectedError: string;
    /** En-tête du bloc de détails ajouté au message. */
    detailsBlockTitle: string;
    /** Préfixe des lignes "Détails" ajoutées au message. */
    detailLabels: {
      company: string;
      event: string;
      passengers: string;
      pickup: string;
      dropoff: string;
      date: string;
      time: string;
      vehicle: string;
      budget: string;
      quoteRequested: string;
    };
  };
  seo: {
    zonesTitle: string;
    zonesText: string;
    proTitle: string;
    proText: string;
    qualityTitle: string;
    qualityText: string;
  };
  cta: {
    text: string;
    link: string;
    href: string;
  };
};

export const CONTACT_UI: I18n<ContactUi> = {
  fr: {
    hero: {
      eyebrow: "Contact",
      title: "Dites‑nous comment nous pouvons vous aider",
      intro:
        "Entreprises, groupes, événements ou renseignements: notre équipe répond rapidement à chaque demande. Vous pouvez aussi demander un devis détaillé.",
      whatsapp: "WhatsApp",
      email: "Email",
    },
    selector: {
      step: "Étape 1",
      heading: "Type de demande",
      help: "Sélectionnez d’abord votre type de demande, puis remplissez le formulaire.",
    },
    useCases: [
      {
        id: "entreprise",
        label: "Entreprise",
        subtitle: "Comptes corporate, besoins récurrents, solutions dédiées",
        subject: "Demande entreprise",
      },
      {
        id: "groupes",
        label: "Groupes & Événements",
        subtitle: "Événements, roadshows, séminaires, mariages",
        subject: "Demande groupes / événements",
      },
      {
        id: "renseignements",
        label: "Renseignements",
        subtitle: "Informations générales et questions diverses",
        subject: "Demande de renseignements",
      },
    ],
    form: {
      intro:
        "Formulaire rapide : les champs essentiels d'abord, puis les détails si besoin.",
      firstName: "Prénom",
      lastName: "Nom",
      email: "Email",
      phone: "Téléphone",
      phonePlaceholder: "+33 6 12 34 56 78",
      phoneTitle: "Format attendu : 06 60 15 98 00 ou +33 6 60 15 98 00",
      phonePattern: FR_PHONE_PATTERN,
      subject: "Sujet",
      message: "Message",
      messagePlaceholder: "Expliquez votre besoin en quelques lignes.",
      quote: "Je souhaite recevoir un devis détaillé",
      detailsSummary: "Ajouter des détails (facultatif)",
      company: "Entreprise",
      eventName: "Nom de l'événement",
      pickup: "Point de départ",
      dropoff: "Destination",
      date: "Date",
      time: "Heure",
      passengers: "Nombre de passagers",
      vehicle: "Véhicule souhaité",
      vehiclePlaceholder: "Berline, SUV, Van...",
      budget: "Budget indicatif",
      budgetPlaceholder: "Ex: 450 EUR",
      honeypotLabel: "Ne pas remplir",
      sending: "Envoi…",
      submit: "Envoyer",
      backHome: "Retour à l’accueil",
      success: "Votre message a été envoyé avec succès.",
      genericError: "Une erreur est survenue",
      unexpectedError: "Erreur inattendue",
      detailsBlockTitle: "Détails",
      detailLabels: {
        company: "Entreprise",
        event: "Événement",
        passengers: "Passagers",
        pickup: "Départ",
        dropoff: "Arrivée",
        date: "Date",
        time: "Heure",
        vehicle: "Véhicule",
        budget: "Budget",
        quoteRequested: "Devis demandé: Oui",
      },
    },
    seo: {
      zonesTitle: "Zones desservies",
      zonesText:
        "Nice, Cannes, Antibes, Monaco, Saint‑Tropez, Menton, Grasse, Saint‑Paul‑de‑Vence et l’ensemble de la Côte d’Azur.",
      proTitle: "Services professionnels",
      proText:
        "Coordination d’événements, déplacements d’équipes, transferts aéroport, accueil VIP, navettes et logistique sur‑mesure.",
      qualityTitle: "Engagement qualité",
      qualityText:
        "Chauffeurs expérimentés et discrets, flotte haut de gamme via partenaires, suivi rigoureux et disponibilité 24/7.",
    },
    cta: {
      text: "Besoin d'une réservation immédiate ?",
      link: "Utiliser le formulaire de réservation",
      href: "/reservation",
    },
  },
  en: {
    hero: {
      eyebrow: "Contact",
      title: "Tell us how we can help you",
      intro:
        "Companies, groups, events or general enquiries: our team responds quickly to every request. You can also ask for a detailed quote.",
      whatsapp: "WhatsApp",
      email: "Email",
    },
    selector: {
      step: "Step 1",
      heading: "Type of request",
      help: "First select your type of request, then fill in the form.",
    },
    useCases: [
      {
        id: "entreprise",
        label: "Business",
        subtitle: "Corporate accounts, recurring needs, dedicated solutions",
        subject: "Business enquiry",
      },
      {
        id: "groupes",
        label: "Groups & Events",
        subtitle: "Events, roadshows, seminars, weddings",
        subject: "Groups / events enquiry",
      },
      {
        id: "renseignements",
        label: "General enquiries",
        subtitle: "General information and miscellaneous questions",
        subject: "General enquiry",
      },
    ],
    form: {
      intro:
        "Quick form: the essential fields first, then the details if needed.",
      firstName: "First name",
      lastName: "Last name",
      email: "Email",
      phone: "Phone",
      phonePlaceholder: "+33 6 12 34 56 78",
      phoneTitle: "Please enter a valid phone number, e.g. +33 6 60 15 98 00",
      phonePattern: INTL_PHONE_PATTERN,
      subject: "Subject",
      message: "Message",
      messagePlaceholder: "Tell us about your needs in a few lines.",
      quote: "I would like to receive a detailed quote",
      detailsSummary: "Add details (optional)",
      company: "Company",
      eventName: "Event name",
      pickup: "Pick-up point",
      dropoff: "Destination",
      date: "Date",
      time: "Time",
      passengers: "Number of passengers",
      vehicle: "Preferred vehicle",
      vehiclePlaceholder: "Sedan, SUV, Van...",
      budget: "Indicative budget",
      budgetPlaceholder: "e.g. 450 EUR",
      honeypotLabel: "Do not fill in",
      sending: "Sending…",
      submit: "Send",
      backHome: "Back to home",
      success: "Your message has been sent successfully.",
      genericError: "An error occurred",
      unexpectedError: "Unexpected error",
      detailsBlockTitle: "Details",
      detailLabels: {
        company: "Company",
        event: "Event",
        passengers: "Passengers",
        pickup: "Pick-up",
        dropoff: "Drop-off",
        date: "Date",
        time: "Time",
        vehicle: "Vehicle",
        budget: "Budget",
        quoteRequested: "Quote requested: Yes",
      },
    },
    seo: {
      zonesTitle: "Areas covered",
      zonesText:
        "Nice, Cannes, Antibes, Monaco, Saint-Tropez, Menton, Grasse, Saint-Paul-de-Vence and the whole French Riviera.",
      proTitle: "Professional services",
      proText:
        "Event coordination, team travel, airport transfers, VIP welcome, shuttles and bespoke logistics.",
      qualityTitle: "Quality commitment",
      qualityText:
        "Experienced and discreet chauffeurs, high-end fleet via partners, rigorous follow-up and 24/7 availability.",
    },
    cta: {
      text: "Need an immediate booking?",
      link: "Use the booking form",
      href: "/en/reservation",
    },
  },
  ar: {
    hero: {
      eyebrow: "تواصل معنا",
      title: "أخبرنا كيف يمكننا مساعدتك",
      intro:
        "شركات، مجموعات، فعاليات أو استفسارات عامة: يردّ فريقنا بسرعة على كل طلب. كما يمكنك طلب عرض سعر مفصّل.",
      whatsapp: "واتساب",
      email: "البريد الإلكتروني",
    },
    selector: {
      step: "الخطوة الأولى",
      heading: "نوع الطلب",
      help: "اختر أولاً نوع طلبك، ثم استكمل النموذج.",
    },
    useCases: [
      {
        id: "entreprise",
        label: "شركات",
        subtitle: "حسابات مؤسسية، احتياجات متكررة، حلول مخصّصة",
        subject: "طلب من شركة",
      },
      {
        id: "groupes",
        label: "مجموعات وفعاليات",
        subtitle: "فعاليات، جولات ترويجية، ندوات، حفلات زفاف",
        subject: "طلب مجموعات / فعاليات",
      },
      {
        id: "renseignements",
        label: "استفسارات عامة",
        subtitle: "معلومات عامة وأسئلة متنوّعة",
        subject: "طلب استفسار",
      },
    ],
    form: {
      intro: "نموذج سريع: الحقول الأساسية أولاً، ثم التفاصيل عند الحاجة.",
      firstName: "الاسم الأول",
      lastName: "اسم العائلة",
      email: "البريد الإلكتروني",
      phone: "الهاتف",
      phonePlaceholder: "+33 6 12 34 56 78",
      phoneTitle: "يرجى إدخال رقم هاتف صحيح، مثال: +33 6 60 15 98 00",
      phonePattern: INTL_PHONE_PATTERN,
      subject: "الموضوع",
      message: "الرسالة",
      messagePlaceholder: "صِف احتياجك في بضعة أسطر.",
      quote: "أرغب في الحصول على عرض سعر مفصّل",
      detailsSummary: "إضافة تفاصيل (اختياري)",
      company: "الشركة",
      eventName: "اسم الفعالية",
      pickup: "نقطة الانطلاق",
      dropoff: "الوجهة",
      date: "التاريخ",
      time: "الوقت",
      passengers: "عدد الركاب",
      vehicle: "السيارة المطلوبة",
      vehiclePlaceholder: "سيدان، دفع رباعي، فان...",
      budget: "الميزانية التقديرية",
      budgetPlaceholder: "مثال: 450 يورو",
      honeypotLabel: "يُرجى عدم التعبئة",
      sending: "جارٍ الإرسال…",
      submit: "إرسال",
      backHome: "العودة إلى الصفحة الرئيسية",
      success: "تم إرسال رسالتك بنجاح.",
      genericError: "حدث خطأ",
      unexpectedError: "خطأ غير متوقّع",
      detailsBlockTitle: "التفاصيل",
      detailLabels: {
        company: "الشركة",
        event: "الفعالية",
        passengers: "الركاب",
        pickup: "الانطلاق",
        dropoff: "الوصول",
        date: "التاريخ",
        time: "الوقت",
        vehicle: "السيارة",
        budget: "الميزانية",
        quoteRequested: "طلب عرض سعر: نعم",
      },
    },
    seo: {
      zonesTitle: "المناطق المغطّاة",
      zonesText:
        "نيس، كان، أنتيب، موناكو، سان تروبيه، مَنتون، غراس، سان بول دو فانس وكامل الريفييرا الفرنسية.",
      proTitle: "خدمات احترافية",
      proText:
        "تنسيق الفعاليات، تنقّل الفرق، النقل من وإلى المطارات، استقبال كبار الشخصيات، الحافلات المكوكية والخدمات اللوجستية المخصّصة.",
      qualityTitle: "التزام بالجودة",
      qualityText:
        "سائقون ذوو خبرة وحصافة، أسطول راقٍ عبر شركاء، متابعة دقيقة وتوفّر على مدار الساعة طوال الأسبوع.",
    },
    cta: {
      text: "بحاجة إلى حجز فوري؟",
      link: "استخدم نموذج الحجز",
      href: "/ar/reservation",
    },
  },
};
